import { type RefObject, useEffect } from 'react';

interface UseOutsideClickOptions {
  excludeRefs?: Array<RefObject<HTMLElement>>;
  enabled?: boolean;
}

/**
 * Hook to detect clicks outside of specified element
 * @param ref - Reference to the element
 * @param callback - Function to call when clicking outside
 * @param options - Optional configuration
 */
export function useOutsideClick(
  ref: RefObject<HTMLElement>,
  callback: () => void,
  options?: UseOutsideClickOptions
): void {
  const { excludeRefs = [], enabled = true } = options || {};

  useEffect(() => {
    if (!enabled) return;

    function handleClickOutside(event: MouseEvent | TouchEvent) {
      const target = event.target as Node;

      // Check if click is inside the main ref
      if (ref.current && ref.current.contains(target)) {
        return;
      }

      // Check if click is inside any excluded refs
      const isInsideExcludedRef = excludeRefs.some(
        (excludeRef) => excludeRef.current && excludeRef.current.contains(target)
      );

      if (isInsideExcludedRef) {
        return;
      }

      // Click is outside all specified elements
      callback();
    }

    // Add event listeners
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [ref, callback, excludeRefs, enabled]);
}
